import { gte, lt } from 'semver';

import config from '../../envConfig';
import { IVersionInfo } from '../../types/interfaces/version-info';

export enum VersionStatus {
  UpToDate = 'upToDate',
  UpdateRecommended = 'updateRecommended',
  UpdateRequired = 'updateRequired',
  Unknown = 'unknown',
}

export interface IVersionCheckResult {
  status: VersionStatus;
  version: string | null;
  lastSupportedVersion: string | null;
  appVersion: string | null;
}

export function checkVersionStatus(
  versionInfo: IVersionInfo | undefined,
): IVersionCheckResult {
  const appVersion = config.version;

  if (!appVersion || !versionInfo) {
    return {
      status: VersionStatus.Unknown,
      version: null,
      lastSupportedVersion: null,
      appVersion: appVersion || null,
    };
  }

  const { currentVersion, lastSupportedVersion } = versionInfo;

  if (!currentVersion || !lastSupportedVersion) {
    return {
      status: VersionStatus.Unknown,
      version: currentVersion || null,
      lastSupportedVersion: lastSupportedVersion || null,
      appVersion,
    };
  }

  const normalizeVersion = (v: string) => v.replace(/^v/i, '');

  const normalizedAppVersion = normalizeVersion(appVersion);
  const normalizedVersion = normalizeVersion(currentVersion);
  const normalizedLastSupportedVersion = normalizeVersion(lastSupportedVersion);

  try {
    if (gte(normalizedAppVersion, normalizedVersion)) {
      return {
        status: VersionStatus.UpToDate,
        version: currentVersion,
        lastSupportedVersion,
        appVersion,
      };
    }

    if (
      gte(normalizedAppVersion, normalizedLastSupportedVersion) &&
      lt(normalizedAppVersion, normalizedVersion)
    ) {
      return {
        status: VersionStatus.UpdateRecommended,
        version: currentVersion,
        lastSupportedVersion,
        appVersion,
      };
    }

    if (lt(normalizedAppVersion, normalizedLastSupportedVersion)) {
      return {
        status: VersionStatus.UpdateRequired,
        version: currentVersion,
        lastSupportedVersion,
        appVersion,
      };
    }

    return {
      status: VersionStatus.Unknown,
      version: currentVersion,
      lastSupportedVersion,
      appVersion,
    };
  } catch (error) {
    console.error('Error parsing version:', error);
    return {
      status: VersionStatus.Unknown,
      version: currentVersion,
      lastSupportedVersion,
      appVersion,
    };
  }
}
